import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, Plus, Trash2, TrendingUp, FileDown, FileSpreadsheet } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';
import { apiClient } from '../../api/client';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const gbp = (pence) => `£${((pence || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const pct = (v) => (v === null || v === undefined ? '—' : `${v}%`);

const CATEGORIES = [
  { value: 'cogs', label: 'COGS (service delivery)' },
  { value: 'opex', label: 'Operating (subscriptions/tools)' },
  { value: 'salary', label: 'Salary' },
  { value: 'marketing', label: 'Marketing (drives CAC)' },
];
const CADENCES = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'annual', label: 'Annual' },
  { value: 'one_time', label: 'One-time' },
];

const EMPTY = { label: '', category: 'opex', cadence: 'monthly', amount_gbp: '', notes: '' };

export const VaultMarginsPanel = () => {
  const [margins, setMargins] = useState(null);
  const [items, setItems] = useState([]);
  const [trend, setTrend] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pubBusy, setPubBusy] = useState(false);
  const [pubMsg, setPubMsg] = useState('');
  const [month, setMonth] = useState('');

  const load = useCallback(async (m) => {
    const q = m ? `?month=${m}` : '';
    const [mg, ci, tr] = await Promise.all([
      apiClient.request(`/api/vault/margins${q}`).catch(() => null),
      apiClient.request('/api/vault/cost-items').catch(() => null),
      apiClient.request('/api/vault/margins/trend?months=12').catch(() => null),
    ]);
    if (mg) setMargins(mg);
    if (ci) setItems(ci.items || []);
    if (tr) setTrend((tr.series || []).map((s) => ({
      month: s.month.slice(2),
      'Gross %': s.gross_margin_pct,
      'Net %': s.net_margin_pct,
      'Revenue £': Math.round((s.revenue_pence || 0) / 100),
    })));
  }, []);

  useEffect(() => { load(); }, [load]);

  const addItem = async () => {
    if (!form.label || form.amount_gbp === '') return;
    setBusy(true);
    try {
      await apiClient.request('/api/vault/cost-items', {
        method: 'POST',
        body: JSON.stringify({ ...form, amount_gbp: parseFloat(form.amount_gbp) }),
      });
      setForm(EMPTY);
      await load(month);
    } catch (e) { alert(e.message); } finally { setBusy(false); }
  };

  const removeItem = async (id) => {
    await apiClient.request(`/api/vault/cost-items/${id}`, { method: 'DELETE' }).catch(() => null);
    load(month);
  };

  const downloadCsv = () => {
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const lines = [
      ['HiBFF Financials', margins.month].map(esc).join(','),
      '',
      ['Metric', 'Value'].map(esc).join(','),
      ...[
        ['Revenue (£)', (margins.revenue_pence / 100).toFixed(2)],
        ['COGS total (£)', (margins.cogs_total_pence / 100).toFixed(2)],
        ['Gross profit (£)', (margins.gross_profit_pence / 100).toFixed(2)],
        ['Gross margin (%)', margins.gross_margin_pct ?? ''],
        ['OpEx + Salaries + Marketing (£)', (margins.opex_total_pence / 100).toFixed(2)],
        ['Net profit (£)', (margins.net_profit_pence / 100).toFixed(2)],
        ['Net margin (%)', margins.net_margin_pct ?? ''],
        ['CAC (£)', margins.cac_pence !== null ? (margins.cac_pence / 100).toFixed(2) : ''],
        ['New paying customers', margins.new_paying_customers],
      ].map((r) => r.map(esc).join(',')),
      '',
      ['Cost item', 'Category', 'Cadence', 'Amount (£)', 'Notes'].map(esc).join(','),
      ...items.map((it) => [it.label, it.category, it.cadence, (it.amount_pence / 100).toFixed(2), it.notes || ''].map(esc).join(',')),
    ].join('\n');
    const blob = new Blob([lines], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `HiBFF-Financials-${margins.month}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const publishToDataRoom = async () => {
    setPubBusy(true);
    setPubMsg('');
    try {
      const q = month ? `?month=${month}` : '';
      const r = await apiClient.request(`/api/vault/margins/publish-to-dataroom${q}`, { method: 'POST' });
      setPubMsg(`In Data Room → 01 · Financials (${r.month})`);
    } catch (e) { setPubMsg(`Failed: ${e.message}`); } finally { setPubBusy(false); }
  };

  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const token = localStorage.getItem('hibff_auth_token') || sessionStorage.getItem('hibff_auth_token');
      const q = month ? `?month=${month}` : '';
      const res = await fetch(`${API_URL}/api/vault/margins/report.pdf${q}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`PDF failed (${res.status})`);
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `HiBFF-Financials-${margins.month}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { alert(e.message); } finally { setPdfBusy(false); }
  };

  if (!margins) return <div className="text-center py-8 text-gray-500"><Loader2 size={18} className="animate-spin mx-auto" /></div>;

  const cards = [
    { label: 'Revenue', value: gbp(margins.revenue_pence), color: '#10B981' },
    { label: 'COGS', value: gbp(margins.cogs_total_pence), sub: `${gbp(margins.usage_cogs_pence)} auto usage`, color: '#EF4444' },
    { label: 'Gross Margin', value: pct(margins.gross_margin_pct), sub: gbp(margins.gross_profit_pence), color: '#0EA5E9' },
    { label: 'OpEx + Salaries', value: gbp(margins.opex_total_pence), color: '#F59E0B' },
    { label: 'Net Margin', value: pct(margins.net_margin_pct), sub: gbp(margins.net_profit_pence), color: margins.net_profit_pence >= 0 ? '#10B981' : '#EF4444' },
    { label: 'CAC', value: margins.cac_pence !== null ? gbp(margins.cac_pence) : '—', sub: `${margins.new_paying_customers} new payer${margins.new_paying_customers === 1 ? '' : 's'}`, color: '#8B5CF6' },
  ];

  return (
    <div className="bg-white rounded-2xl border-2 border-gray-200 p-5" data-testid="vault-margins-panel">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
          <TrendingUp size={14} className="text-emerald-600" /> Margins & Cost Ledger
          <span className="text-xs font-normal text-gray-400">{margins.month}</span>
        </h3>
        <div className="flex items-center gap-2">
          <button onClick={downloadCsv} className="flex items-center gap-1 text-[11px] px-2.5 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50" data-testid="margins-csv-btn">
            <FileSpreadsheet size={12} /> CSV
          </button>
          <button onClick={downloadPdf} disabled={pdfBusy} className="flex items-center gap-1 text-[11px] px-2.5 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50" data-testid="margins-pdf-btn">
            {pdfBusy ? <Loader2 size={12} className="animate-spin" /> : <FileDown size={12} />} PDF
          </button>
          <button onClick={publishToDataRoom} disabled={pubBusy} className="flex items-center gap-1 text-[11px] px-2.5 py-1.5 rounded-lg font-semibold text-white bg-purple-600 hover:bg-purple-700" data-testid="margins-publish-btn" title="Regenerates the financials PDF and places it in the investor Data Room (01 · Financials)">
            {pubBusy ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Data Room
          </button>
          <input type="month" value={month} onChange={(e) => { setMonth(e.target.value); load(e.target.value); }}
            className="text-xs border border-gray-200 rounded-lg px-2 py-1 text-gray-600" data-testid="margins-month-picker" />
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
        {cards.map((c) => (
          <div key={c.label} className="bg-gray-50 rounded-xl p-3 text-center" data-testid={`margin-card-${c.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
            <p className="text-[10px] text-gray-500 uppercase tracking-wide">{c.label}</p>
            <p className="text-lg font-black" style={{ color: c.color }}>{c.value}</p>
            {c.sub && <p className="text-[10px] text-gray-400">{c.sub}</p>}
          </div>
        ))}
      </div>

      {pubMsg && <p className={`text-[11px] mb-3 ${pubMsg.startsWith('Failed') ? 'text-red-600' : 'text-emerald-600'}`} data-testid="publish-msg">✓ {pubMsg}</p>}

      {/* Margin trend — last 12 months */}
      {trend.length > 0 && (
        <div className="mb-5" data-testid="margins-trend-chart">
          <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-2">Margin trend (12 months)</p>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={trend} margin={{ top: 4, right: 8, bottom: 0, left: -18 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
              <XAxis dataKey="month" tick={{ fontSize: 9 }} />
              <YAxis tick={{ fontSize: 9 }} unit="%" />
              <Tooltip contentStyle={{ fontSize: 11 }} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              <Line type="monotone" dataKey="Gross %" stroke="#0EA5E9" strokeWidth={2} dot={{ r: 2 }} connectNulls />
              <Line type="monotone" dataKey="Net %" stroke="#10B981" strokeWidth={2} dot={{ r: 2 }} connectNulls />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Add cost item */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3">
        <input placeholder="Label (e.g. Emergent Pro, Salary — Robert)" value={form.label}
          onChange={(e) => setForm({ ...form, label: e.target.value })}
          className="col-span-2 px-3 py-2 rounded-lg border border-gray-200 text-xs" data-testid="cost-item-label" />
        <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
          className="px-2 py-2 rounded-lg border border-gray-200 text-xs bg-white text-gray-700" data-testid="cost-item-category">
          {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
        <select value={form.cadence} onChange={(e) => setForm({ ...form, cadence: e.target.value })}
          className="px-2 py-2 rounded-lg border border-gray-200 text-xs bg-white text-gray-700" data-testid="cost-item-cadence">
          {CADENCES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
        <div className="flex gap-1">
          <input type="number" min="0" step="0.01" placeholder="£" value={form.amount_gbp}
            onChange={(e) => setForm({ ...form, amount_gbp: e.target.value })}
            className="w-full px-3 py-2 rounded-lg border border-gray-200 text-xs" data-testid="cost-item-amount" />
          <button onClick={addItem} disabled={busy}
            className="px-3 py-2 rounded-lg bg-emerald-600 text-white" data-testid="cost-item-add">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
          </button>
        </div>
      </div>

      {/* Ledger */}
      <div className="space-y-1">
        {items.length === 0 && <p className="text-xs text-gray-400 text-center py-2">No cost items yet — add your subscriptions and salaries above.</p>}
        {items.map((it) => (
          <div key={it.item_id} className="flex items-center justify-between text-xs px-3 py-2 rounded-lg bg-gray-50" data-testid={`cost-item-row-${it.item_id}`}>
            <span className="font-medium text-gray-800">{it.label}
              <span className="ml-2 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase" style={{
                backgroundColor: { cogs: '#FEE2E2', opex: '#FEF3C7', salary: '#EDE9FE', marketing: '#DBEAFE' }[it.category],
                color: { cogs: '#B91C1C', opex: '#B45309', salary: '#6D28D9', marketing: '#1D4ED8' }[it.category],
              }}>{it.category}</span>
            </span>
            <span className="flex items-center gap-3 text-gray-600">
              {gbp(it.amount_pence)} <span className="text-gray-400">/{it.cadence === 'one_time' ? 'once' : it.cadence === 'annual' ? 'yr' : 'mo'}</span>
              <button onClick={() => removeItem(it.item_id)} className="text-gray-300 hover:text-red-500" data-testid={`cost-item-delete-${it.item_id}`}>
                <Trash2 size={12} />
              </button>
            </span>
          </div>
        ))}
      </div>
      <p className="text-[10px] text-gray-400 mt-3">{margins.notes} CAC = monthly marketing spend ÷ new paying customers.</p>
    </div>
  );
};

export default VaultMarginsPanel;
